import React from 'react';
import { AlertCircle, ArrowRight, TimerReset } from 'lucide-react';

import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { formatCurrencyAmount } from '../../lib/currency';
import { EmptyState } from '../common/States';

const SuperadminPendingPaymentsPanel = ({ summary, rows, onNavigate }) => {
  return (
    <Card className="animate-slide-up overflow-hidden border-warning/25 bg-gradient-to-br from-card via-card to-warning/5">
      <CardHeader className="border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TimerReset className="h-4 w-4 text-amber-600" />
              Pending Payments
            </CardTitle>
            <CardDescription>Outstanding invoices and collections at risk</CardDescription>
          </div>
          <button
            type="button"
            onClick={() => onNavigate?.('billing/pending')}
            className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-amber-600"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 pt-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-medium text-muted-foreground">Pending Total</p>
            <p className="mt-2 text-2xl font-semibold text-foreground">{formatCurrencyAmount(summary.payments_pending || 0, 'INR')}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-medium text-muted-foreground">Pending Rows</p>
            <p className="mt-2 text-2xl font-semibold text-foreground">{rows?.length || 0}</p>
          </div>
        </div>

        <div className="space-y-3">
          {(rows || []).slice(0, 5).map((row, index) => (
            <div
              key={row.id}
              className="animate-slide-up rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-warning/30 hover:shadow-sm"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{row.tenant_name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{row.billing_account_name}</p>
                </div>
                <Badge variant={row.status === 'overdue' ? 'danger' : 'warning'}>{row.status}</Badge>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Outstanding</span>
                <span className="font-semibold text-amber-600">{formatCurrencyAmount(row.outstanding_amount || 0, row.currency_code || 'INR')}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Due Date</span>
                <span className="inline-flex items-center gap-1 font-medium text-foreground">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {row.due_at ? new Date(row.due_at).toLocaleDateString() : 'Unscheduled'}
                </span>
              </div>
            </div>
          ))}
          {(!rows || rows.length === 0) && (
            <EmptyState
              compact
              icon={AlertCircle}
              title="No pending payments"
              description="Outstanding invoices at risk of overdue will appear here."
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SuperadminPendingPaymentsPanel;
